import { motion } from "framer-motion";
import { Briefcase, Sunrise, HeartHandshake, FlaskConical } from "lucide-react";
import { EASE, Reveal } from "./Weave";

const COMMUNITY_IMG = "/photos/peer-online.webp";

const PILLARS = [
  {
    icon: Briefcase,
    color: "#4AA0A4",
    span: "md:col-span-7",
    title: "Practical business education",
    body: "Real-world ready, never theoretical. The journey from employee to founder, taught by people who have made it — designed for senior professionals by ex-senior professionals.",
  },
  {
    icon: Sunrise,
    color: "#E93E36",
    span: "md:col-span-5",
    title: "Sophrology-informed mindset & leadership",
    body: "Mind-body practices that build the calm, capacity and presence founding genuinely asks of you.",
  },
  {
    icon: HeartHandshake,
    color: "#F4B847",
    span: "md:col-span-5",
    title: "Mentoring & a peer cohort",
    body: "Ex-operators who have sat where you sit, plus a like-minded cohort making the same move at the same time.",
  },
  {
    icon: FlaskConical,
    color: "#0E2F32",
    span: "md:col-span-7",
    title: "Real-world experimentation",
    body: "Small, safe tests with actual customers — evidence over opinion, so the decision you make is truly yours.",
  },
];

const STAGES = [
  {
    weeks: "Weeks 1–2",
    chip: "bg-teallight text-tealdark",
    title: "Understand where you are",
    body: "Coaching, experienced mentoring and Sophrology mind-body practices help you reconnect with what actually matters — not just what's on your CV.",
  },
  {
    weeks: "Weeks 3–5",
    chip: "bg-redlight text-wred",
    title: "Understand what you bring",
    body: "Identify the skills, strengths, relationships and resources you already hold. You'll complete your Founder Capacity Index — a structured, non-judgemental mirror.",
  },
  {
    weeks: "Weeks 6–8",
    chip: "bg-yellowlight text-[#9A6B0F]",
    title: "Explore whether it can fit your life",
    body: "Work through the real implications for time, energy, relationships and finances. A qualified IFA joins, and business advisors map the journey. No finished concept required.",
  },
  {
    weeks: "Weeks 9–10",
    chip: "bg-ink text-paper",
    title: "Make an informed choice",
    body: "Bring everything together. You'll leave with a clear next step — whatever that turns out to be — and the confidence to take it.",
  },
];

export default function Programme() {
  return (
    <section id="programme" data-testid="pillars-bento" className="scroll-mt-24">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
        <Reveal>
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-tealdark">The programme</p>
          <h2 className="max-w-3xl text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Founder first. Business second.
          </h2>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-body2">
            Building a business doesn&rsquo;t start with a business plan. It starts with you — your
            energy, your values, your capacity to keep going when things don&rsquo;t go to plan. The
            Academy year weaves five strands into one journey.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-12 lg:gap-7">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
              className={`group rounded-3xl border border-paperborder bg-white p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-24px_rgba(14,47,50,0.35)] lg:p-10 ${p.span}`}
            >
              <div
                className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110"
                style={{ backgroundColor: `${p.color}22` }}
              >
                <p.icon className="h-6 w-6" style={{ color: p.color }} />
              </div>
              <h3 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">{p.title}</h3>
              <p className="mt-3 leading-relaxed text-body2">{p.body}</p>
            </motion.div>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="relative mt-16 overflow-hidden rounded-3xl border border-paperborder">
            <img
              src={COMMUNITY_IMG}
              alt="A Weave peer cohort in conversation"
              loading="lazy"
              className="h-64 w-full object-cover sm:h-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent" />
            <p className="absolute bottom-6 left-6 right-6 text-base font-medium text-paper sm:left-9 sm:text-lg">
              Learning online, together — peer sessions with a like-minded cohort, people alongside you making the same move.
            </p>
          </div>
        </Reveal>

        <div className="mt-20 lg:mt-28">
          <Reveal>
            <h3 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
              Ten weeks, four stages
            </h3>
            <p className="mt-4 max-w-2xl text-lg leading-relaxed text-body2">
              Personal Clarity is the first ten weeks — a complete, standalone programme, and the
              doorway into the full Academy year. It&rsquo;s delivered online, in the early evenings
              and occasionally at weekends, with a mix of learning styles — so your day job stays
              intact.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-7" data-testid="stages-timeline">
            {STAGES.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
                className="relative flex h-full flex-col rounded-3xl border border-paperborder bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-24px_rgba(14,47,50,0.35)]"
              >
                <span className="absolute right-6 top-6 text-5xl font-extrabold text-papermuted" aria-hidden="true">
                  {i + 1}
                </span>
                <span className={`w-max rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider ${s.chip}`}>
                  {s.weeks}
                </span>
                <h4 className="mt-5 text-lg font-semibold leading-snug text-ink">{s.title}</h4>
                <p className="mt-3 text-sm leading-relaxed text-body2">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
